import React, {useCallback, useEffect, useMemo, useState} from "react";
import axios from "axios";
import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
} from "@material-tailwind/react";
import {
    ArrowPathIcon,
    BoltIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    StopCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {
    AUTOMATION_EVENT_LABELS,
    AUTOMATION_REASON_LABELS,
    evaluateAutomationItem,
} from "./automationEligibility";
import AutomationItemSelector from './AutomationItemSelector';

const TERMINAL_STATUSES = ['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED', 'CANCELLED'];
const SUCCESS_ITEM_STATUSES = ['EVENT_SAVED', 'CHAT_CLOSED'];

const formatStatus = (value) => (value || '')
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatDateTime = (value) => {
    if (!value) return '/';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
};

const itemStatusClass = (status) => {
    if (SUCCESS_ITEM_STATUSES.includes(status)) {
        return 'border-green-200 bg-green-50 text-green-900 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-200';
    }
    if (status === 'FAILED') {
        return 'border-red-200 bg-red-50 text-red-900 dark:border-panda-pink/30 dark:bg-panda-pink/10 dark:text-panda-pink';
    }
    if (['SKIPPED_ALREADY_HAS_CASE', 'SKIPPED_NOT_DUE'].includes(status)) {
        return 'border-gray-200 bg-gray-50 text-gray-600 dark:border-dark-700 dark:bg-dark-850 dark:text-gray-400';
    }
    return 'border-cyan-200 bg-cyan-50 text-cyan-950 dark:border-panda-cyan/30 dark:bg-panda-cyan/10 dark:text-panda-cyan';
};

export default function AutomationDialog({
    open,
    order,
    onClose,
    apiUrl,
    requestHeaders,
    onOrderRefresh,
}) {
    const [profileNumber, setProfileNumber] = useState('');
    const [interItemDelaySeconds, setInterItemDelaySeconds] = useState('15');
    const [job, setJob] = useState(null);
    const [agent, setAgent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showNewRun, setShowNewRun] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState([]);

    const evaluatedItems = useMemo(() => (
        (order?.items || []).map(item => ({
            ...item,
            automation: evaluateAutomationItem(item, new Date(), order),
        }))
    ), [order]);
    const candidates = useMemo(
        () => evaluatedItems.filter(item => item.automation.eligible),
        [evaluatedItems]
    );
    const selectorItems = evaluatedItems.map(item => ({
        itemId: item.id,
        title: item.automation.automationTitle || item.title,
        originalTitle: item.title,
        ...item.automation,
    }));
    const selectedCandidates = candidates.filter(item => selectedItemIds.includes(item.id));

    useEffect(() => {
        setSelectedItemIds(current => current.filter(id => candidates.some(item => item.id === id)));
    }, [candidates]);
    const parsedProfile = Number.parseInt(profileNumber, 10);
    const profileIsValid = /^\d{1,2}$/.test(profileNumber.trim())
        && parsedProfile >= 1
        && parsedProfile <= 99;
    const normalizedProfile = profileIsValid ? String(parsedProfile).padStart(2, '0') : '--';
    const parsedDelay = interItemDelaySeconds.trim() === ''
        ? 15
        : Number.parseInt(interItemDelaySeconds, 10);
    const delayIsValid = interItemDelaySeconds.trim() === ''
        || (/^\d{1,5}$/.test(interItemDelaySeconds.trim())
            && parsedDelay >= 0
            && parsedDelay <= 86400);
    const isTerminal = job ? TERMINAL_STATUSES.includes(job.status) : false;
    const activeJob = job && !isTerminal;

    const fetchAgentStatus = useCallback(async () => {
        try {
            const response = await axios.get(`${apiUrl}/automation/agents/status`, {headers: requestHeaders});
            setAgent(response.status === 204 ? null : response.data);
        } catch (requestError) {
            console.error('Error fetching automation agent status:', requestError);
            setAgent(null);
        }
    }, [apiUrl, requestHeaders]);

    const fetchJob = useCallback(async (jobId) => {
        if (!jobId) return null;
        const response = await axios.get(`${apiUrl}/automation/jobs/${jobId}`, {headers: requestHeaders});
        const nextJob = response.data;
        setJob(nextJob);
        if ((nextJob.items || []).some(item => item.referenceNumber)) {
            onOrderRefresh?.(order.id);
        }
        return nextJob;
    }, [apiUrl, onOrderRefresh, order?.id, requestHeaders]);

    useEffect(() => {
        if (!open || !order?.id) return undefined;
        let cancelled = false;
        setProfileNumber(order.automationProfileNumber == null
            ? ''
            : String(order.automationProfileNumber));
        setInterItemDelaySeconds('15');
        setIsLoading(true);
        setError('');
        setShowNewRun(false);
        setSelectedItemIds([]);

        Promise.all([
            axios.get(`${apiUrl}/automation/orders/${order.id}/latest-job`, {headers: requestHeaders}),
            axios.get(`${apiUrl}/automation/agents/status`, {headers: requestHeaders}),
        ]).then(([jobResponse, agentResponse]) => {
            if (cancelled) return;
            setJob(jobResponse.status === 204 ? null : jobResponse.data);
            setAgent(agentResponse.status === 204 ? null : agentResponse.data);
        }).catch(requestError => {
            if (cancelled) return;
            setError(requestError.response?.data?.error || 'Could not load automation status.');
        }).finally(() => {
            if (!cancelled) setIsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [apiUrl, open, order?.id, order?.automationProfileNumber, requestHeaders]);

    useEffect(() => {
        if (!open) return undefined;
        const interval = setInterval(() => {
            fetchAgentStatus();
            if (job?.jobId && !TERMINAL_STATUSES.includes(job.status)) {
                fetchJob(job.jobId).catch(requestError => {
                    console.error('Error polling automation job:', requestError);
                });
            }
        }, 2500);
        return () => clearInterval(interval);
    }, [fetchAgentStatus, fetchJob, job?.jobId, job?.status, open]);

    const startJob = async () => {
        if (!profileIsValid || !delayIsValid || !order?.id || selectedCandidates.length === 0) return;
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(`${apiUrl}/automation/jobs`, {
                orderId: order.id,
                profileNumber: parsedProfile,
                interItemDelaySeconds: parsedDelay,
                itemIds: selectedCandidates.map(item => item.id),
            }, {headers: requestHeaders});
            setJob(response.data);
            setShowNewRun(false);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Could not create automation job.');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelJob = async () => {
        if (!job?.jobId) return;
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${apiUrl}/automation/jobs/${job.jobId}/cancel`,
                {},
                {headers: requestHeaders}
            );
            setJob(response.data);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Could not cancel automation job.');
        } finally {
            setIsLoading(false);
        }
    };

    const retryItem = async (jobItemId) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(
                `${apiUrl}/automation/jobs/${job.jobId}/items/${jobItemId}/retry`,
                {},
                {headers: requestHeaders}
            );
            setJob(response.data);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Could not retry failed item.');
        } finally {
            setIsLoading(false);
        }
    };

    const renderStartForm = !job || (isTerminal && showNewRun);

    return (
        <Dialog
            open={Boolean(open)}
            handler={onClose}
            size="lg"
            className="max-h-[calc(100vh-3rem)] overflow-hidden dark:bg-dark-900"
        >
            <DialogHeader className="flex items-center justify-between dark:text-gray-100">
                <span className="flex items-center gap-2">
                    <BoltIcon className="h-5 w-5 text-cyan-800 dark:text-panda-cyan" />
                    WoB automation
                </span>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-dark-800"
                    title="Close"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </DialogHeader>

            <DialogBody className="max-h-[calc(100vh-12rem)] overflow-x-hidden overflow-y-auto">
                <div className="min-w-0 space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-700 dark:bg-dark-850">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{order?.wobOrderNumber || 'No WOB number'}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{order?.email || '/'}</p>
                    </div>

                    <div className={`rounded-lg border p-3 ${
                        agent?.online
                            ? 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10'
                            : 'border-amber-200 bg-amber-50 dark:border-amber-400/30 dark:bg-amber-400/10'
                    }`}>
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    Local agent: {agent?.online ? 'Online' : 'Offline'}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {agent?.machineName || 'No agent heartbeat received'}
                                    {agent?.lastHeartbeatAt ? ` · ${formatDateTime(agent.lastHeartbeatAt)}` : ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={fetchAgentStatus}
                                className="rounded-md p-1.5 text-gray-600 hover:bg-white/70 dark:text-gray-300 dark:hover:bg-dark-800"
                                title="Refresh agent status"
                            >
                                <ArrowPathIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-panda-pink/30 dark:bg-panda-pink/10 dark:text-panda-pink">
                            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {renderStartForm ? (
                        <>
                            <div className="grid min-w-0 gap-3 md:grid-cols-2">
                                <Input
                                    label="Dolphin / Espanso profile number"
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={profileNumber}
                                    readOnly
                                    className="dark:text-gray-100"
                                    labelProps={{className: "dark:text-gray-400"}}
                                />
                                <Input
                                    label="Delay between items (seconds)"
                                    type="number"
                                    min="0"
                                    max="86400"
                                    value={interItemDelaySeconds}
                                    onChange={event => setInterItemDelaySeconds(event.target.value)}
                                    error={!delayIsValid}
                                    className="dark:text-gray-100"
                                    labelProps={{className: "dark:text-gray-400"}}
                                />
                            </div>
                            <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 dark:border-panda-cyan/30 dark:bg-panda-cyan/10">
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                    Stored profile: <span className="font-mono font-bold">{normalizedProfile}</span>
                                    {' · '}Dolphin prefix {normalizedProfile}. · Espanso /start{normalizedProfile}
                                </p>
                            </div>
                            {!profileIsValid && (
                                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                                    Add a Dolphin / Espanso profile number (1–99) by editing this order first.
                                </p>
                            )}

                            <AutomationItemSelector items={selectorItems} selectedIds={selectedCandidates.map(item => item.id)}
                                onChange={setSelectedItemIds} disabled={isLoading} />
                            {candidates.length === 0 && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                                No item currently meets the automation timing rules.
                            </p>}
                        </>
                    ) : (
                        <>
                            <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 dark:border-panda-cyan/30 dark:bg-panda-cyan/10">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <p className="text-sm font-semibold text-cyan-950 dark:text-panda-cyan">
                                            {formatStatus(job.status)}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">{job.statusMessage || '/'}</p>
                                    </div>
                                    <span className="rounded-md bg-white/80 px-2 py-1 font-mono text-xs text-gray-700 dark:bg-dark-900/70 dark:text-gray-300">
                                        Profile {String(job.profileNumber).padStart(2, '0')}
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                    Delay between items: {job.interItemDelaySeconds ?? 15}s
                                </p>
                                {job.dolphinProfileName && (
                                    <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                        Dolphin: {job.dolphinProfileName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    Progress ({(job.items || []).filter(item => SUCCESS_ITEM_STATUSES.includes(item.status)).length}/
                                    {(job.items || []).filter(item => ![
                                        'SKIPPED_ALREADY_HAS_CASE',
                                        'SKIPPED_NOT_DUE',
                                    ].includes(item.status)).length})
                                </p>
                                <div className="space-y-2">
                                    {(job.items || []).map(item => (
                                        <div key={item.jobItemId} className={`rounded-lg border p-3 ${itemStatusClass(item.status)}`}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold">{item.position + 1}. {item.title}</p>
                                                    <p className="text-xs opacity-80">
                                                        {formatStatus(item.status)}{item.phase ? ` · ${formatStatus(item.phase)}` : ''}
                                                    </p>
                                                    {item.eligibilityReason && (
                                                        <p className="mt-1 text-xs opacity-80">
                                                            {AUTOMATION_REASON_LABELS[item.eligibilityReason]
                                                                || formatStatus(item.eligibilityReason)}
                                                            {item.targetEventType
                                                                ? ` · ${AUTOMATION_EVENT_LABELS[item.targetEventType]
                                                                    || formatStatus(item.targetEventType)}`
                                                                : ''}
                                                        </p>
                                                    )}
                                                    {item.referenceNumber && (
                                                        <p className="mt-1 font-mono text-xs">Reference: {item.referenceNumber}</p>
                                                    )}
                                                    {item.error && <p className="mt-1 text-xs">{item.error}</p>}
                                                </div>
                                                {item.status === 'FAILED' && isTerminal && item.retryable && (
                                                    <button
                                                        type="button"
                                                        onClick={() => retryItem(item.jobItemId)}
                                                        disabled={isLoading}
                                                        className="mv-secondary-button shrink-0"
                                                    >
                                                        <ArrowPathIcon className="h-4 w-4" />
                                                        Retry
                                                    </button>
                                                )}
                                                {item.status === 'FAILED' && !item.retryable && (
                                                    <span className="shrink-0 rounded-md border border-red-200 bg-white/70 px-2 py-1 text-xs font-semibold dark:border-panda-pink/30 dark:bg-dark-900/40">
                                                        Manual review required
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {(job.logs || []).length > 0 && (
                                <details className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-700 dark:bg-dark-850">
                                    <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Job log ({job.logs.length})
                                    </summary>
                                    <div className="mt-2 max-h-48 space-y-1 overflow-y-auto font-mono text-xs text-gray-600 dark:text-gray-400">
                                        {job.logs.map(log => (
                                            <p key={log.id || `${log.createdAt}-${log.message}`}>
                                                {formatDateTime(log.createdAt)} [{log.level}] {log.message}
                                            </p>
                                        ))}
                                    </div>
                                </details>
                            )}
                        </>
                    )}
                </div>
            </DialogBody>

            <DialogFooter className="flex items-center justify-end gap-2">
                <Button variant="text" color="black" onClick={onClose} className="mv-secondary-button">
                    <XMarkIcon className="h-4 w-4" />
                    Close
                </Button>
                {renderStartForm && (
                    <Button
                        onClick={startJob}
                        disabled={isLoading || !profileIsValid || !delayIsValid || selectedCandidates.length === 0 || Boolean(activeJob)}
                        className="mv-primary-button"
                    >
                        <BoltIcon className="h-4 w-4" />
                        {isLoading ? 'Starting...' : 'Start automation'}
                    </Button>
                )}
                {activeJob && (
                    <Button
                        onClick={cancelJob}
                        disabled={isLoading || job.cancelRequested}
                        className="mv-secondary-button"
                    >
                        <StopCircleIcon className="h-4 w-4" />
                        {job.cancelRequested ? 'Cancelling...' : 'Cancel job'}
                    </Button>
                )}
                {isTerminal && !showNewRun && (
                    <Button onClick={() => {setShowNewRun(true); setSelectedItemIds([]);}} className="mv-primary-button">
                        <CheckCircleIcon className="h-4 w-4" />
                        New automation
                    </Button>
                )}
            </DialogFooter>
        </Dialog>
    );
}
